import React, { useEffect, useState } from 'react';
import {
    Box,
    Button,
    Checkbox,
    Chip,
    Divider,
    FormControl,
    FormControlLabel,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography,
    IconButton,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    ToggleButtonGroup,
    ToggleButton,
    Autocomplete
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faEdit, faPlus } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import {
    FileTypeLicenseOptionResponse,
    LicenseOptionDto,
    LicenseOptionAggregationMode,
    LicenseOptionType,
    CreateLicenseOptionRequest
} from '../../apiClient/Api';

interface LicenseOptionsSectionProps {
    licenseOptions: FileTypeLicenseOptionResponse[];
    availableLicenseOptions: LicenseOptionDto[];
    selectedLicenseOptionId: string;
    setSelectedLicenseOptionId: (id: string) => void;
    isRequired: boolean;
    setIsRequired: (required: boolean) => void;
    fileTypeSpecificDefault: string;
    setFileTypeSpecificDefault: (value: string) => void;
    serviceEinheiten: string;
    setServiceEinheiten: (value: string) => void;
    onAddLicenseOption: () => void;
    onRemoveLicenseOption: (optionId: string) => void;
    onUpdateLicenseOption: (
        optionId: string,
        isRequired: boolean,
        fileTypeSpecificDefault: string,
        isVisibleToCustomer: boolean,
        aggregationMode: LicenseOptionAggregationMode,
        serviceEinheiten: string,
    ) => void;
    onCreateNewOption: (option: CreateLicenseOptionRequest) => void;
    onRefreshAvailableOptions: () => Promise<void>;
}

/**
 * Returns the type label for a license option type
 */
const getLicenseOptionTypeLabel = (type?: LicenseOptionType): string => {
    switch (type) {
        case LicenseOptionType.Number:
            return 'Number';
        case LicenseOptionType.Boolean:
            return 'Boolean';
        case LicenseOptionType.String:
            return 'String';
        case LicenseOptionType.Date:
            return 'Date';
        default:
            return 'Unknown';
    }
};

/**
 * Returns the label for a license option aggregation mode.
 */
const getAggregationModeLabel = (mode: LicenseOptionAggregationMode, t: (key: string) => string): string => {
    switch (mode) {
        case LicenseOptionAggregationMode.Sum:
            return t('fileTypeEdit.licenseOptions.aggregationMode.sum');
        case LicenseOptionAggregationMode.Max:
            return t('fileTypeEdit.licenseOptions.aggregationMode.max');
        case LicenseOptionAggregationMode.None:
        default:
            return t('fileTypeEdit.licenseOptions.aggregationMode.none');
    }
};

/**
 * Component for managing license options assigned to a FileType
 */
export const LicenseOptionsSection: React.FC<LicenseOptionsSectionProps> = ({
    licenseOptions,
    availableLicenseOptions,
    selectedLicenseOptionId,
    setSelectedLicenseOptionId,
    isRequired,
    setIsRequired,
    fileTypeSpecificDefault,
    setFileTypeSpecificDefault,
    serviceEinheiten,
    setServiceEinheiten,
    onAddLicenseOption,
    onRemoveLicenseOption,
    onUpdateLicenseOption,
    onCreateNewOption,
    onRefreshAvailableOptions
}) => {
    const { t } = useTranslation();
    
    // Dialog states
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogMode, setDialogMode] = useState<'create' | 'edit' | 'link'>('create');
    const [editingOption, setEditingOption] = useState<FileTypeLicenseOptionResponse | null>(null);
    
    // States für neue/editierte Option
    const [newOptionName, setNewOptionName] = useState('');
    const [newOptionIdentifier, setNewOptionIdentifier] = useState('');
    const [newOptionType, setNewOptionType] = useState<LicenseOptionType>(LicenseOptionType.String);
    const [newOptionDefaultValue, setNewOptionDefaultValue] = useState('');
    const [newOptionDescription, setNewOptionDescription] = useState('');
    const [newOptionAggregationMode, setNewOptionAggregationMode] = useState<LicenseOptionAggregationMode>(LicenseOptionAggregationMode.None);
    const [dialogIsRequired, setDialogIsRequired] = useState(false);
    const [dialogSpecificDefault, setDialogSpecificDefault] = useState('');
    const [selectedExistingOptionId, setSelectedExistingOptionId] = useState('');
    const [newOptionIsVisibleToCustomer, setNewOptionIsVisibleToCustomer] = useState(true);
    const [dialogIsVisibleToCustomer, setDialogIsVisibleToCustomer] = useState(true);
    const [dialogAggregationMode, setDialogAggregationMode] = useState<LicenseOptionAggregationMode>(LicenseOptionAggregationMode.None);
    const [dialogServiceEinheiten, setDialogServiceEinheiten] = useState('');

    /**
     * Opens dialog for creating a new option
     */
    const handleOpenCreateDialog = () => {
        setDialogMode('create');
        setEditingOption(null);
        setNewOptionName('');
        setNewOptionIdentifier('');
        setNewOptionType(LicenseOptionType.String);
        setNewOptionDefaultValue('');
        setNewOptionDescription('');
        setNewOptionAggregationMode(LicenseOptionAggregationMode.None);
        setDialogIsRequired(false);
        setDialogSpecificDefault('');
        setSelectedExistingOptionId('');
        setNewOptionIsVisibleToCustomer(true);
        setDialogIsVisibleToCustomer(true);
        setDialogAggregationMode(LicenseOptionAggregationMode.None);
        setDialogServiceEinheiten('');
        setDialogOpen(true);
    };

    /**
     * Opens dialog for linking an existing option
     */
    const handleOpenLinkDialog = async () => {
        setDialogMode('link');
        setEditingOption(null);
        setDialogIsRequired(false);
        setDialogSpecificDefault('');
        setSelectedExistingOptionId('');
        setDialogOpen(true);
        
        // Refresh available options when opening the link dialog
        await onRefreshAvailableOptions();
    };

    /**
     * Opens dialog for editing an existing option
     */
    const handleOpenEditDialog = (option: FileTypeLicenseOptionResponse) => {
        setDialogMode('edit');
        setEditingOption(option);
        setDialogIsRequired(option.isRequired ?? false);
        setDialogSpecificDefault(option.fileTypeSpecificDefault ?? '');
        setDialogIsVisibleToCustomer(option.licenseOption?.isVisibleToCustomer ?? true);
        setDialogAggregationMode(option.licenseOption?.aggregationMode ?? LicenseOptionAggregationMode.None);
        setDialogServiceEinheiten(option.serviceEinheiten != null ? String(option.serviceEinheiten) : '');
        setDialogOpen(true);
    };

    /**
     * Closes the dialog
     */
    const handleCloseDialog = () => {
        setDialogOpen(false);
        setEditingOption(null);
        setDialogMode('create');
    };

    /**
     * Saves the option (create, link or update)
     */
    const handleSaveOption = () => {
        if (dialogMode === 'edit' && editingOption) {
            // Update existing option
            onUpdateLicenseOption(
                editingOption.id!,
                dialogIsRequired,
                dialogSpecificDefault,
                dialogIsVisibleToCustomer,
                dialogAggregationMode,
                dialogServiceEinheiten
            );
            handleCloseDialog();
        } else if (dialogMode === 'link') {
            // Link existing option - set states first, then call the function after state update
            setSelectedLicenseOptionId(selectedExistingOptionId);
            setIsRequired(dialogIsRequired);
            setFileTypeSpecificDefault(dialogSpecificDefault);
            setServiceEinheiten(dialogServiceEinheiten);
            
            // Use setTimeout to ensure state is updated before calling the function
            handleCloseDialog();
            setTimeout(() => {
                onAddLicenseOption();
            }, 0);
        } else {
            // Create new option
            const newOption: CreateLicenseOptionRequest = {
                commonName: newOptionName,
                identifier: newOptionIdentifier,
                type: newOptionType,
                aggregationMode: newOptionAggregationMode,
                defaultValue: newOptionDefaultValue || null,
                description: newOptionDescription || null,
                isActive: true,
                displayOrder: 0,
                isVisibleToCustomer: newOptionIsVisibleToCustomer
            };
            setIsRequired(dialogIsRequired);
            setFileTypeSpecificDefault(dialogSpecificDefault);
            setServiceEinheiten(dialogServiceEinheiten);
            onCreateNewOption(newOption);
            handleCloseDialog();
        }
    };

    return (
        <>
            <Divider sx={{ my: 3 }} />
            <Box sx={{ marginY: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">
                        {t('fileTypeEdit.licenseOptions.title')}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                            variant="outlined"
                            startIcon={<FontAwesomeIcon icon={faPlus} />}
                            onClick={handleOpenLinkDialog}
                        >
                            {t('fileTypeEdit.licenseOptions.linkExisting')}
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={<FontAwesomeIcon icon={faPlus} />}
                            onClick={handleOpenCreateDialog}
                        >
                            {t('fileTypeEdit.licenseOptions.createNew')}
                        </Button>
                    </Box>
                </Box>

                {licenseOptions.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', my: 2 }}>
                        {t('fileTypeEdit.noLicenseOptions')}
                    </Typography>
                ) : (
                    <List>
                        {licenseOptions.map((option) => (
                            <ListItem
                                key={option.id}
                                sx={{
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: 1,
                                    mb: 1,
                                    bgcolor: 'background.paper'
                                }}
                            >
                                <ListItemText
                                    primary={
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Typography variant="subtitle1">
                                                {option.licenseOption?.commonName}
                                            </Typography>
                                            {option.isRequired && (
                                                <Chip label={t('fileTypeEdit.required')} color="error" size="small" />
                                            )}
                                            {option.licenseOption?.isVisibleToCustomer === false && (
                                                <Chip label={t('fileTypeEdit.licenseOptions.hiddenFromCustomer')} color="warning" size="small" />
                                            )}
                                        </Box>
                                    }
                                    secondary={
                                        <Box sx={{ mt: 0.5 }}>
                                            <Typography variant="body2" color="text.secondary">
                                                <strong>{t('fileTypeEdit.identifier')}:</strong> {option.licenseOption?.identifier} | 
                                                <strong> {t('fileTypeEdit.type')}:</strong> {getLicenseOptionTypeLabel(option.licenseOption?.type)}
                                            </Typography>
                                            {option.serviceEinheiten != null && (
                                                <Typography variant="body2" color="text.secondary">
                                                    <strong>{t('fileTypeEdit.licenseOptions.serviceUnits')}:</strong> {option.serviceEinheiten}
                                                </Typography>
                                            )}
                                            {option.licenseOption?.defaultValue && (
                                                <Typography variant="body2" color="text.secondary">
                                                    <strong>{t('fileTypeEdit.licenseOptions.overview.globalDefault')}:</strong> {option.licenseOption.defaultValue}
                                                    {option.fileTypeSpecificDefault && (
                                                        <Chip 
                                                            label={`${t('fileTypeEdit.licenseOptions.overview.overridesGlobal')}: ${option.fileTypeSpecificDefault}`} 
                                                            color="warning" 
                                                            size="small" 
                                                            sx={{ ml: 1 }}
                                                        />
                                                    )}
                                                </Typography>
                                            )}
                                            {!option.licenseOption?.defaultValue && option.fileTypeSpecificDefault && (
                                                <Typography variant="body2" color="text.secondary">
                                                    <strong>{t('fileTypeEdit.defaultValue')}:</strong> {option.fileTypeSpecificDefault}
                                                </Typography>
                                            )}
                                        </Box>
                                    }
                                />
                                <ListItemSecondaryAction>
                                    <IconButton
                                        edge="end"
                                        onClick={() => handleOpenEditDialog(option)}
                                        sx={{ mr: 1 }}
                                    >
                                        <FontAwesomeIcon icon={faEdit} />
                                    </IconButton>
                                    <IconButton
                                        edge="end"
                                        onClick={() => onRemoveLicenseOption(option.id!)}
                                        color="error"
                                    >
                                        <FontAwesomeIcon icon={faTrash} />
                                    </IconButton>
                                </ListItemSecondaryAction>
                            </ListItem>
                        ))}
                    </List>
                )}
            </Box>

            {/* Dialog für Erstellen/Editieren/Verlinken */}
            <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
                <DialogTitle>
                    {dialogMode === 'edit' 
                        ? t('fileTypeEdit.licenseOptions.editOption')
                        : dialogMode === 'link'
                        ? t('fileTypeEdit.licenseOptions.linkExisting')
                        : t('fileTypeEdit.licenseOptions.createNew')}
                </DialogTitle>
                <DialogContent>
                    {dialogMode === 'edit' ? (
                        // Edit Mode: Required, Specific Default und Visible to Customer
                        <Box sx={{ mt: 2 }}>
                            <Typography variant="subtitle1" gutterBottom>
                                {editingOption?.licenseOption?.commonName}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                {editingOption?.licenseOption?.identifier} ({getLicenseOptionTypeLabel(editingOption?.licenseOption?.type)})
                            </Typography>
                            <Grid container spacing={2} sx={{ mt: 2 }}>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label={t('fileTypeEdit.specificDefault')}
                                        value={dialogSpecificDefault}
                                        onChange={(e) => setDialogSpecificDefault(e.target.value)}
                                        helperText={t('fileTypeEdit.specificDefaultHelp')}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        type="number"
                                        label={t('fileTypeEdit.licenseOptions.serviceUnits')}
                                        value={dialogServiceEinheiten}
                                        onChange={(e) => setDialogServiceEinheiten(e.target.value)}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <FormControl fullWidth>
                                        <InputLabel>{t('fileTypeEdit.licenseOptions.aggregationMode.label')}</InputLabel>
                                        <Select
                                            label={t('fileTypeEdit.licenseOptions.aggregationMode.label')}
                                            value={dialogAggregationMode}
                                            onChange={(e) =>
                                                setDialogAggregationMode(e.target.value as LicenseOptionAggregationMode)
                                            }
                                            disabled={editingOption?.licenseOption?.type !== LicenseOptionType.Number}
                                        >
                                            <MenuItem value={LicenseOptionAggregationMode.None}>
                                                {getAggregationModeLabel(LicenseOptionAggregationMode.None, t)}
                                            </MenuItem>
                                            <MenuItem value={LicenseOptionAggregationMode.Sum}>
                                                {getAggregationModeLabel(LicenseOptionAggregationMode.Sum, t)}
                                            </MenuItem>
                                            <MenuItem value={LicenseOptionAggregationMode.Max}>
                                                {getAggregationModeLabel(LicenseOptionAggregationMode.Max, t)}
                                            </MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={dialogIsRequired}
                                                onChange={(e) => setDialogIsRequired(e.target.checked)}
                                            />
                                        }
                                        label={t('fileTypeEdit.required')}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={dialogIsVisibleToCustomer}
                                                onChange={(e) => setDialogIsVisibleToCustomer(e.target.checked)}
                                            />
                                        }
                                        label={t('fileTypeEdit.licenseOptions.visibleToCustomer')}
                                    />
                                </Grid>
                            </Grid>
                        </Box>
                    ) : dialogMode === 'link' ? (
                        // Link Mode: Existierende Option auswählen
                        <Box sx={{ mt: 2 }}>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <Autocomplete
                                        options={availableLicenseOptions.filter(opt => !licenseOptions.some(lo => lo.licenseOptionId === opt.id))}
                                        getOptionLabel={(option) => option.commonName || ''}
                                        value={availableLicenseOptions.find(opt => opt.id === selectedExistingOptionId) || null}
                                        onChange={(event, newValue) => {
                                            setSelectedExistingOptionId(newValue?.id || '');
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label={t('fileTypeEdit.selectOption')}
                                                required
                                                helperText={`${availableLicenseOptions.filter(opt => !licenseOptions.some(lo => lo.licenseOptionId === opt.id)).length} ${t('fileTypeEdit.licenseOptions.optionsAvailable')}`}
                                            />
                                        )}
                                        renderOption={(props, option) => {
                                            const { key, ...otherProps } = props;
                                            return (
                                                <li key={option.id} {...otherProps}>
                                                    <Box>
                                                        <Typography variant="body1">
                                                            {option.commonName}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {option.identifier} • {getLicenseOptionTypeLabel(option.type)}
                                                            {option.defaultValue && ` • Default: ${option.defaultValue}`}
                                                        </Typography>
                                                    </Box>
                                                </li>
                                            );
                                        }}
                                        noOptionsText={t('fileTypeEdit.licenseOptions.noOptionsAvailable')}
                                        isOptionEqualToValue={(option, value) => option.id === value.id}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label={t('fileTypeEdit.specificDefault')}
                                        value={dialogSpecificDefault}
                                        onChange={(e) => setDialogSpecificDefault(e.target.value)}
                                        helperText={t('fileTypeEdit.specificDefaultHelp')}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        type="number"
                                        label={t('fileTypeEdit.licenseOptions.serviceUnits')}
                                        value={dialogServiceEinheiten}
                                        onChange={(e) => setDialogServiceEinheiten(e.target.value)}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={dialogIsRequired}
                                                onChange={(e) => setDialogIsRequired(e.target.checked)}
                                            />
                                        }
                                        label={t('fileTypeEdit.required')}
                                    />
                                </Grid>
                            </Grid>
                        </Box>
                    ) : (
                        // Create Mode: alle Felder
                        <Grid container spacing={2} sx={{ mt: 1 }}>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    required
                                    label={t('fileTypeEdit.licenseOptions.commonName')}
                                    value={newOptionName}
                                    onChange={(e) => setNewOptionName(e.target.value)}
                                    placeholder="z.B. Maximale Benutzer"
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    required
                                    label={t('fileTypeEdit.licenseOptions.identifier')}
                                    value={newOptionIdentifier}
                                    onChange={(e) => setNewOptionIdentifier(e.target.value)}
                                    placeholder="z.B. max_users"
                                    helperText={t('fileTypeEdit.licenseOptions.identifierHelper')}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth required>
                                    <InputLabel>{t('fileTypeEdit.licenseOptions.type')}</InputLabel>
                                    <Select
                                        value={newOptionType}
                                        onChange={(e) => setNewOptionType(e.target.value as LicenseOptionType)}
                                        label={t('fileTypeEdit.licenseOptions.type')}
                                    >
                                        <MenuItem value={LicenseOptionType.Number}>
                                            {getLicenseOptionTypeLabel(LicenseOptionType.Number)}
                                        </MenuItem>
                                        <MenuItem value={LicenseOptionType.Boolean}>
                                            {getLicenseOptionTypeLabel(LicenseOptionType.Boolean)}
                                        </MenuItem>
                                        <MenuItem value={LicenseOptionType.String}>
                                            {getLicenseOptionTypeLabel(LicenseOptionType.String)}
                                        </MenuItem>
                                        <MenuItem value={LicenseOptionType.Date}>
                                            {getLicenseOptionTypeLabel(LicenseOptionType.Date)}
                                        </MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label={t('fileTypeEdit.licenseOptions.defaultValue')}
                                    value={newOptionDefaultValue}
                                    onChange={(e) => setNewOptionDefaultValue(e.target.value)}
                                    placeholder="z.B. 10"
                                    helperText={t('fileTypeEdit.licenseOptions.defaultValueHelper')}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    type="number"
                                    label={t('fileTypeEdit.licenseOptions.serviceUnits')}
                                    value={dialogServiceEinheiten}
                                    onChange={(e) => setDialogServiceEinheiten(e.target.value)}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth>
                                    <InputLabel>{t('fileTypeEdit.licenseOptions.aggregationMode.label')}</InputLabel>
                                    <Select
                                        label={t('fileTypeEdit.licenseOptions.aggregationMode.label')}
                                        value={newOptionAggregationMode}
                                        onChange={(e) =>
                                            setNewOptionAggregationMode(e.target.value as LicenseOptionAggregationMode)
                                        }
                                        disabled={newOptionType !== LicenseOptionType.Number}
                                    >
                                        <MenuItem value={LicenseOptionAggregationMode.None}>
                                            {getAggregationModeLabel(LicenseOptionAggregationMode.None, t)}
                                        </MenuItem>
                                        <MenuItem value={LicenseOptionAggregationMode.Sum}>
                                            {getAggregationModeLabel(LicenseOptionAggregationMode.Sum, t)}
                                        </MenuItem>
                                        <MenuItem value={LicenseOptionAggregationMode.Max}>
                                            {getAggregationModeLabel(LicenseOptionAggregationMode.Max, t)}
                                        </MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label={t('fileTypeEdit.licenseOptions.description')}
                                    value={newOptionDescription}
                                    onChange={(e) => setNewOptionDescription(e.target.value)}
                                    placeholder="Optionale Beschreibung für diese Lizenz-Option"
                                    multiline
                                    rows={2}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={dialogIsRequired}
                                            onChange={(e) => setDialogIsRequired(e.target.checked)}
                                        />
                                    }
                                    label={t('fileTypeEdit.licenseOptions.required')}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={newOptionIsVisibleToCustomer}
                                            onChange={(e) => setNewOptionIsVisibleToCustomer(e.target.checked)}
                                        />
                                    }
                                    label={t('fileTypeEdit.licenseOptions.visibleToCustomer')}
                                />
                            </Grid>
                        </Grid>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>
                        {t('fileTypeEdit.cancel')}
                    </Button>
                    <Button
                        onClick={handleSaveOption}
                        variant="contained"
                        disabled={
                            (dialogMode === 'create' && (!newOptionName || !newOptionIdentifier)) ||
                            (dialogMode === 'link' && !selectedExistingOptionId)
                        }
                    >
                        {t('fileTypeEdit.save')}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};
